import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { toast } from "sonner";

import { ProductShell } from "@/components/app/product-shell";
import { ErrorBoundary } from "@/components/error-boundary";
import {
  getLandAccess,
  saveLandScreen,
  screenParcel,
  type ScreenParcelResult,
} from "@/lib/land/land.functions";
import { LAND_SELLER_PROMPTS, TX_EQUITABLE_INTEREST_DISCLOSURE } from "@/lib/land/seller-call";
import type { TriState } from "@/lib/land/deal-killers";

export const Route = createFileRoute("/_authenticated/app/land")({
  head: () => ({
    meta: [
      { title: "Land Deal Desk — AROS FLOW" },
      {
        name: "description",
        content:
          "Screen a parcel for legal use, physical feasibility, infrastructure, demand, buyers and value — with an evidence ledger behind every fact.",
      },
      { property: "og:title", content: "Land Deal Desk — AROS FLOW" },
      {
        property: "og:description",
        content: "What can legally, physically and economically be done with this parcel?",
      },
      { property: "og:type", content: "website" },
    ],
  }),
  component: LandPage,
});

const money = (n: number) => `$${Math.round(n).toLocaleString()}`;

function riskClass(risk: string) {
  return risk === "RED"
    ? "text-red-400 border-red-500/40 bg-red-500/10"
    : risk === "GREEN"
      ? "text-emerald-400 border-emerald-500/40 bg-emerald-500/10"
      : "text-amber-300 border-amber-500/40 bg-amber-500/10";
}

function Pill({ risk, children }: { risk: string; children: React.ReactNode }) {
  return (
    <span
      className={`rounded-md border px-2 py-0.5 text-[10px] uppercase tracking-wide ${riskClass(risk)}`}
    >
      {children}
    </span>
  );
}

function Field({
  label,
  value,
  onChange,
  type = "text",
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
}) {
  return (
    <label className="flex flex-col gap-1 text-xs">
      <span className="text-muted-foreground">{label}</span>
      <input
        type={type}
        value={value}
        placeholder={placeholder ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="focus-key rounded-md border border-[var(--glass-border)] bg-white/[0.03] px-2.5 py-2 text-sm"
      />
    </label>
  );
}

function TriField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: TriState;
  onChange: (v: TriState) => void;
}) {
  return (
    <label className="flex flex-col gap-1 text-xs">
      <span className="text-muted-foreground">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as TriState)}
        className="focus-key rounded-md border border-[var(--glass-border)] bg-white/[0.03] px-2.5 py-2 text-sm"
      >
        <option value="unknown">Unknown</option>
        <option value="yes">Yes — verified</option>
        <option value="no">No — verified</option>
      </select>
    </label>
  );
}

/** Buyer-first land sequence — the order this desk is meant to be worked in. */
const LAND_SEQUENCE = [
  "Buyer Audience / Buy Box",
  "Search Area",
  "Subject Parcel",
  "Due Diligence",
  "Need / Solution / Fit",
  "Match / Cash Offer / Assignment / Partnership",
  "Close",
];

function SequenceCue() {
  return (
    <div className="mb-4 rounded-xl border border-[var(--glass-border)] bg-card/60 px-3 py-2">
      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
        Buyer-first sequence
      </div>
      <ol className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[11px] text-white/80">
        {LAND_SEQUENCE.map((step, i) => (
          <li key={step} className="flex items-center gap-1.5">
            {i > 0 && (
              <span className="text-white/30" aria-hidden>
                →
              </span>
            )}
            <span>{step}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}

function LandPage() {
  return (
    <ProductShell
      title="Land Deal Desk"
      subtitle="What can legally, physically and economically be done with this parcel? Every fact carries its source, date and confidence — and unknown stays unknown."
    >
      <SequenceCue />
      <ErrorBoundary label="Land Mode crashed">
        <LandBody />
      </ErrorBoundary>
    </ProductShell>
  );
}

function LandBody() {
  const checkAccess = useServerFn(getLandAccess);
  const runScreen = useServerFn(screenParcel);
  const persist = useServerFn(saveLandScreen);

  const access = useQuery({ queryKey: ["land-access"], queryFn: () => checkAccess() });

  const [form, setForm] = useState({
    addressText: "",
    apn: "",
    county: "",
    acreage: "",
    frontageFt: "",
    askingPrice: "",
    buyerMax: "",
    newHomePrice: "",
    assignmentFee: "15000",
    closingCosts: "2500",
    diligence: "1500",
    riskReserve: "2500",
  });
  const [manual, setManual] = useState<{
    legalAccess: TriState;
    physicalAccess: TriState;
    zoningPermitsUse: TriState;
    publicWater: TriState;
    publicSewer: TriState;
    recordedRestrictionBlocksUse: TriState;
    zoning: string;
  }>({
    legalAccess: "unknown",
    physicalAccess: "unknown",
    zoningPermitsUse: "unknown",
    publicWater: "unknown",
    publicSewer: "unknown",
    recordedRestrictionBlocksUse: "unknown",
    zoning: "",
  });
  const [result, setResult] = useState<ScreenParcelResult | null>(null);

  const screen = useMutation({
    mutationFn: () =>
      runScreen({
        data: {
          addressText: form.addressText.trim() || undefined,
          apn: form.apn.trim() || undefined,
          county: form.county.trim() || undefined,
          acreage: Number(form.acreage) || 0,
          frontageFt: Number(form.frontageFt) || undefined,
          manual: { ...manual, zoning: manual.zoning || undefined },
          seller: {
            vacant: true,
            absenteeOrOutOfState: false,
            inherited: false,
            expiredOrCanceledListing: false,
            taxStage: "none",
            statedUrgency: "none",
            askingPrice: Number(form.askingPrice) || null,
          },
          offer: {
            buyerMax: Number(form.buyerMax) || null,
            buyerMaxIsConfirmed: Number(form.buyerMax) > 0,
            newHomePrice: Number(form.newHomePrice) || null,
            assignmentFee: Number(form.assignmentFee) || 0,
            closingCosts: Number(form.closingCosts) || 0,
            diligenceAllowance: Number(form.diligence) || 0,
            riskReserve: Number(form.riskReserve) || 0,
          },
        },
      }),
    onSuccess: (r) => setResult(r),
    onError: (e: Error) => toast.error(e.message),
  });

  const save = useMutation({
    mutationFn: () => persist({ data: { screen: result! } }),
    onSuccess: () => toast.success("Parcel and evidence saved"),
    onError: (e: Error) => toast.error(e.message),
  });

  if (access.isLoading) return <p className="text-sm text-muted-foreground">Checking access…</p>;

  if (!access.data?.enabled) {
    return (
      <div className="rounded-xl border border-[var(--glass-border)] bg-card p-5">
        <h2 className="display text-lg">Land Mode is in closed beta</h2>
        <p className="mt-2 text-sm text-muted-foreground">{access.data?.reason}</p>
        <p className="mt-3 text-xs text-muted-foreground">
          Property Mode is unchanged and available under Analyze.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <section className="rounded-xl border border-[var(--glass-border)] bg-card p-4">
        <h2 className="display text-base">Parcel</h2>
        <p className="mt-1 text-xs text-muted-foreground">
          The canonical key is APN plus county — a street address alone is not enough for vacant
          land.
        </p>
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Field
            label="Street address or nearest address"
            value={form.addressText}
            onChange={(v) => setForm({ ...form, addressText: v })}
          />
          <Field
            label="APN / parcel ID"
            value={form.apn}
            onChange={(v) => setForm({ ...form, apn: v })}
          />
          <Field
            label="County"
            value={form.county}
            onChange={(v) => setForm({ ...form, county: v })}
            placeholder="Bell"
          />
          <Field
            label="Acreage"
            type="number"
            value={form.acreage}
            onChange={(v) => setForm({ ...form, acreage: v })}
          />
          <Field
            label="Road frontage (ft)"
            type="number"
            value={form.frontageFt}
            onChange={(v) => setForm({ ...form, frontageFt: v })}
          />
          <Field
            label="Zoning designation"
            value={manual.zoning}
            onChange={(v) => setManual({ ...manual, zoning: v })}
          />
          <TriField
            label="Legal access"
            value={manual.legalAccess}
            onChange={(v) => setManual({ ...manual, legalAccess: v })}
          />
          <TriField
            label="Physical access"
            value={manual.physicalAccess}
            onChange={(v) => setManual({ ...manual, physicalAccess: v })}
          />
          <TriField
            label="Zoning permits intended use"
            value={manual.zoningPermitsUse}
            onChange={(v) => setManual({ ...manual, zoningPermitsUse: v })}
          />
          <TriField
            label="Public water service area"
            value={manual.publicWater}
            onChange={(v) => setManual({ ...manual, publicWater: v })}
          />
          <TriField
            label="Public sewer service area"
            value={manual.publicSewer}
            onChange={(v) => setManual({ ...manual, publicSewer: v })}
          />
          <TriField
            label="Recorded restriction blocks use"
            value={manual.recordedRestrictionBlocksUse}
            onChange={(v) => setManual({ ...manual, recordedRestrictionBlocksUse: v })}
          />
          <Field
            label="Seller ask"
            type="number"
            value={form.askingPrice}
            onChange={(v) => setForm({ ...form, askingPrice: v })}
          />
          <Field
            label="Confirmed buyer max"
            type="number"
            value={form.buyerMax}
            onChange={(v) => setForm({ ...form, buyerMax: v })}
          />
          <Field
            label="New-build price (for modeled buyer max)"
            type="number"
            value={form.newHomePrice}
            onChange={(v) => setForm({ ...form, newHomePrice: v })}
          />
          <Field
            label="Assignment fee"
            type="number"
            value={form.assignmentFee}
            onChange={(v) => setForm({ ...form, assignmentFee: v })}
          />
          <Field
            label="Closing costs"
            type="number"
            value={form.closingCosts}
            onChange={(v) => setForm({ ...form, closingCosts: v })}
          />
          <Field
            label="Diligence allowance"
            type="number"
            value={form.diligence}
            onChange={(v) => setForm({ ...form, diligence: v })}
          />
          <Field
            label="Risk reserve"
            type="number"
            value={form.riskReserve}
            onChange={(v) => setForm({ ...form, riskReserve: v })}
          />
        </div>
        <button
          type="button"
          onClick={() => screen.mutate()}
          disabled={screen.isPending}
          className="focus-key mt-4 rounded-md gradient-gold px-4 py-2 text-sm font-semibold text-black disabled:opacity-60"
        >
          {screen.isPending ? "Screening parcel…" : "Screen this parcel"}
        </button>
      </section>

      {result && (
        <>
          <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Tile label="Buildability" value={`${result.scores.buildability.score}/100`} />
            <Tile label="Builder demand" value={`${result.scores.demand.score}/100`} />
            <Tile label="Motivation" value={`${result.scores.motivation.score}/100`} />
            <Tile
              label="Lead priority"
              value={
                result.scores.priority.tier === "BLOCKED"
                  ? "Blocked"
                  : `${result.scores.priority.score} · ${result.scores.priority.tier}`
              }
            />
          </section>

          <section className={`rounded-xl border p-4 ${riskClass(result.dealKillers.gate)}`}>
            <h2 className="display text-base">Deal killer gate — {result.dealKillers.gate}</h2>
            <p className="mt-1 text-xs">{result.dealKillers.summary}</p>
            <ul className="mt-3 space-y-2">
              {result.dealKillers.killers.map((k) => (
                <li
                  key={k.code}
                  className="rounded-md border border-white/10 bg-black/20 p-2.5 text-xs"
                >
                  <div className="flex items-center gap-2">
                    <Pill risk={k.risk}>{k.risk}</Pill>
                    <span className="font-semibold">{k.label}</span>
                  </div>
                  <p className="mt-1 text-muted-foreground">{k.detail}</p>
                  <p className="mt-1 text-muted-foreground">Next: {k.resolutionPath}</p>
                </li>
              ))}
              {result.dealKillers.killers.length === 0 && (
                <li className="text-xs">Nothing flagged yet.</li>
              )}
            </ul>
          </section>

          <section className="rounded-xl border border-[var(--glass-border)] bg-card p-4">
            <h2 className="display text-base">Physical and infrastructure screen</h2>
            <div className="mt-3 grid gap-3 text-xs sm:grid-cols-2 lg:grid-cols-4">
              <Tile label="Flood zone" value={result.overlays.floodZone ?? "Needs verification"} />
              <Tile
                label="Floodway"
                value={
                  result.overlays.floodwayPresence === "detected"
                    ? "Detected near subject — share of parcel needs verification"
                    : result.overlays.floodwayPresence === "none"
                      ? "None at screened point — parcel extent needs verification"
                      : "Needs verification"
                }
              />
              <Tile
                label="Wetland indicator"
                value={
                  result.overlays.wetlandPresence === "detected"
                    ? "Detected near subject — share of parcel needs verification"
                    : result.overlays.wetlandPresence === "none"
                      ? "None mapped at screened point"
                      : "Needs verification"
                }
              />
              <Tile
                label="Usable remainder"
                value={
                  result.overlays.usablePct == null
                    ? "Needs verification"
                    : `${result.overlays.usablePct}%`
                }
              />
              <Tile
                label="Preliminary septic risk"
                value={
                  result.overlays.septicRisk === "unknown"
                    ? "Needs verification"
                    : result.overlays.septicRisk
                }
              />
              <Tile
                label="Elevation"
                value={
                  result.overlays.elevationFt
                    ? `${Math.round(result.overlays.elevationFt)} ft`
                    : "Needs verification"
                }
              />
              <Tile label="Parcel class" value={result.classification.label} />
              <Tile
                label="Valuation method"
                value={result.classification.valuationMethod.replace(/_/g, " ")}
              />
            </div>
            <p className="mt-3 text-[11px] text-muted-foreground">
              Screening only. Flood and wetland results are presence checks at the subject location
              — the share of the parcel affected is not measured until real parcel and overlay
              geometry are intersected. Nothing here is a determination, approval or proof of
              buildability.
            </p>
          </section>

          <section className="rounded-xl border border-[var(--glass-border)] bg-card p-4">
            <h2 className="display text-base">Offer — buyer first</h2>
            <p className="mt-1 text-xs text-muted-foreground">{result.buyerMaxNote}</p>
            <div className="mt-3 space-y-1 text-sm">
              {result.offer.lines.map((l) => (
                <div key={l.label} className="flex justify-between border-b border-white/5 py-1">
                  <span className="text-muted-foreground">{l.label}</span>
                  <span>{money(l.amount)}</span>
                </div>
              ))}
            </div>
            <div className="mt-3 grid gap-3 sm:grid-cols-3">
              <Tile label="Opening" value={money(result.offer.openingOffer)} />
              <Tile label="Target" value={money(result.offer.targetOffer)} />
              <Tile label="Maximum seller price" value={money(result.offer.maximumOffer)} />
            </div>
            <ul className="mt-3 space-y-1 text-[11px] text-muted-foreground">
              {result.offer.notes.map((n) => (
                <li key={n}>• {n}</li>
              ))}
            </ul>
          </section>

          <section className="rounded-xl border border-[var(--glass-border)] bg-card p-4">
            <h2 className="display text-base">Evidence ledger</h2>
            <p className="mt-1 text-xs text-muted-foreground">
              {Math.round(result.evidenceCompleteness * 100)}% of entries are verified. Evidence
              status and risk status are separate.
            </p>
            <div className="mt-3 space-y-2">
              {result.evidence.map((e, i) => (
                <div
                  key={`${e.topic}-${i}`}
                  className="rounded-md border border-[var(--glass-border)] p-2.5 text-xs"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <Pill risk={e.riskStatus}>{e.riskStatus}</Pill>
                    <span className="rounded-md border border-white/10 px-2 py-0.5 text-[10px] uppercase">
                      {e.evidenceStatus}
                    </span>
                    <span className="text-[10px] uppercase text-muted-foreground">
                      {e.confidence} confidence
                    </span>
                    {e.sellerClaim && (
                      <span className="text-[10px] uppercase text-amber-300">seller claim</span>
                    )}
                    <span className="font-semibold">{e.label}</span>
                  </div>
                  <p className="mt-1">{e.value ?? "Unknown"}</p>
                  <p className="mt-1 text-muted-foreground">
                    {e.source ?? "No source"}
                    {e.sourceDate ? ` · effective ${e.sourceDate}` : ""} · checked{" "}
                    {new Date(e.retrievedAt).toLocaleDateString()}
                  </p>
                  {e.notes && <p className="mt-1 text-muted-foreground">{e.notes}</p>}
                  {e.verificationRequired && (
                    <p className="mt-1 text-amber-300">Verify: {e.verificationRequired}</p>
                  )}
                </div>
              ))}
            </div>
          </section>

          <section className="rounded-xl border border-[var(--glass-border)] bg-card p-4">
            <h2 className="display text-base">Record sources for this market</h2>
            <ul className="mt-2 space-y-1 text-xs">
              {result.sources.map((s) => (
                <li key={s.url}>
                  <a
                    className="focus-key text-gold hover:underline"
                    href={s.url}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {s.name}
                  </a>{" "}
                  <span className="text-muted-foreground">
                    {s.automated ? "· queried automatically" : "· manual lookup"}
                  </span>
                </li>
              ))}
            </ul>
          </section>

          <section className="rounded-xl border border-[var(--glass-border)] bg-card p-4">
            <h2 className="display text-base">Seller call prompts</h2>
            <ol className="mt-2 list-decimal space-y-1 pl-5 text-xs">
              {LAND_SELLER_PROMPTS.map((p) => (
                <li key={p.key}>
                  {p.ask}
                  <span className="text-muted-foreground">
                    {" "}
                    — answers are stored as seller claims only.
                  </span>
                </li>
              ))}
            </ol>
            <p className="mt-3 rounded-md border border-amber-500/30 bg-amber-500/5 p-2.5 text-[11px] text-amber-200">
              {TX_EQUITABLE_INTEREST_DISCLOSURE.title}. {TX_EQUITABLE_INTEREST_DISCLOSURE.body} (
              {TX_EQUITABLE_INTEREST_DISCLOSURE.references.join(", ")})
            </p>
          </section>

          <button
            type="button"
            onClick={() => save.mutate()}
            disabled={save.isPending}
            className="focus-key rounded-md border border-[var(--gold)]/40 px-4 py-2 text-sm text-gold disabled:opacity-60"
          >
            {save.isPending ? "Saving…" : "Save parcel, evidence and diligence tasks"}
          </button>
        </>
      )}
    </div>
  );
}

function Tile({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-[var(--glass-border)] bg-white/[0.02] p-3">
      <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{label}</div>
      <div className="mt-1 text-sm">{value}</div>
    </div>
  );
}
